import { useQueryClient } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import toast from "react-hot-toast";
import { supabase } from "../../lib/supabase";
import { queryKeys } from "../../lib/queryKeys";

/**
 * Patch a task link in place, so it keeps its id, its position in the list and
 * its created_at. The task dialog used to edit a link by deleting the row and
 * re-inserting it, which churned all three — and lost the link outright if the
 * insert failed after the delete had already landed.
 */
export function useUpdateTaskLink() {
  const queryClient = useQueryClient();

  return async (
    linkId: string,
    updates: { url?: string; description?: string }
  ): Promise<boolean> => {
    const { error } = await supabase
      .from("task_links")
      .update(updates)
      .eq("id", linkId);

    if (error) {
      Sentry.captureException(error, {
        extra: { mutation: "useUpdateTaskLink", linkId },
      });
      toast.error("Failed to update link. Please try again.");
      return false;
    }

    queryClient.invalidateQueries({ queryKey: queryKeys.tasks });
    return true;
  };
}

/**
 * The sub-task half of the pair. Before this existed, submitting the sub-task
 * dialog's pre-filled edit form fell through to the add path and inserted a
 * duplicate link, leaving the original untouched.
 */
export function useUpdateSubtaskLink() {
  const queryClient = useQueryClient();

  return async (
    linkId: string,
    updates: { url?: string; description?: string }
  ): Promise<boolean> => {
    const { error } = await supabase
      .from("subtask_links")
      .update(updates)
      .eq("id", linkId);

    if (error) {
      Sentry.captureException(error, {
        extra: { mutation: "useUpdateSubtaskLink", linkId },
      });
      toast.error("Failed to update link. Please try again.");
      return false;
    }

    queryClient.invalidateQueries({ queryKey: queryKeys.tasks });
    return true;
  };
}
